import { Injectable } from '@angular/core';
import { Feature, LineString, Point } from '@turf/turf';

import * as _ from 'lodash';
import * as turf from '@turf/turf';
import { BehaviorSubject, Observable } from 'rxjs';

export enum DRAW_ROUTING_STATE {
    DRAW = 'draw',
    SELECT = 'select',
    UNKNOWN = 'unknown'
}

export enum DRAW_MODE {
    BEFORE = 'before',
    MIDDLE = 'middle',
    AFTER = 'after',
    UNKNOWN = 'unknown'
}

@Injectable({
    providedIn: 'root'
})
export class DrawRoutingService {
    public startCoordinate: number[];
    public endCoordinate: number[];

    // all observables
    public drawChange$: Observable<Feature<any>[]>;
    public isLoading$: Observable<boolean>;

    private _drawChange$: BehaviorSubject<Feature<any>[]> = new BehaviorSubject([]); // the objects to be draw in the map
    private _isLoading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private _constructedNewFeature: Feature;

    // linesegments we want to remove
    private _removedLineSegmentIds: number[] = [];

    // the manually clicked segments and coordinates
    private _clickedCoordinates: number[][] = [];
    private _clickedLineSegments: GeoJSON.Feature<any>[] = [];

    // inital linesegments
    private _lineSegments: GeoJSON.Feature<any>[] = [];
    private _wayPoints: number[][] = [];

    private _drawMode: DRAW_MODE = DRAW_MODE.UNKNOWN;

    // latest saved state!
    private _latestSegments: Feature<LineString>[];
    private _latestStops: Feature<Point>[];
    private _defaultLine: LineString;

    constructor(
    ) {
        this.drawChange$ = this._drawChange$.asObservable();
        this.isLoading$ = this._isLoading$.asObservable();
    }

    public get drawMode() {
        return this._drawMode;
    }

    public get feature() {
        return this._constructedNewFeature;
    }

    public get drawFeatures() {
        return this._drawChange$.value;
    }

    public getPrevPoint(): number[] {
        let prevPoint: any = _.last(this._clickedCoordinates);

        if (!prevPoint && this._drawMode !== DRAW_MODE.UNKNOWN) {
            prevPoint = this.startCoordinate;
        }

        return prevPoint;
    }

    public toggleLineSegment(lineId: any): void {
        let selected = true;
        // returns if selected or not (to be removed)
        if (_.includes(this._removedLineSegmentIds, lineId)) {
            this._removedLineSegmentIds = _.without(this._removedLineSegmentIds, lineId);
            selected = false;
        } else {
            this._removedLineSegmentIds.push(lineId);
        }

        // update
        const segment = this._lineSegments[lineId];
        segment.properties.mode = selected ? 'selected' : 'view';
        this._triggerFeaturesToRender();
    }

    public addNewLineSegment(newLineSegment: GeoJSON.Feature<any>, pos: number[]) {
        this._clickedLineSegments.push(newLineSegment);
        this._clickedCoordinates.push(pos);

        if (this._drawMode === DRAW_MODE.AFTER) {
            this.endCoordinate = pos;
        }

        this._calculateNewLineString();
        this._triggerFeaturesToRender();
    }

    public hasStartedDrawLine() {
        return this.getPrevPoint() !== undefined;
    }

    public hasStartedEdit() {
        return this.hasStartedDrawLine() || this._removedLineSegmentIds.length > 0;
    }

    public initBeforeOrAfterEdit(start: number[], mode: DRAW_MODE) {
        this._isLoading$.next(true);
        this._drawMode = mode;
        this.startCoordinate = start;

        // override the start or end coordinate depending on which mode we are in
        if (this._drawMode === DRAW_MODE.BEFORE) {
            this.endCoordinate = this._getFirstCoordinate();
        } else if (this._drawMode === DRAW_MODE.AFTER) {
            this.startCoordinate = this._getLastCoordinate();
            this.endCoordinate = undefined;
        }

        this._triggerFeaturesToRender();
        this._isLoading$.next(false);
    }

    public clear() {
        this._clearRoutedLine();
        this._clear();
    }

    public resetDraw() {
        // reset latest draw to the init mode
        this._clearRoutedLine();
        this._initSelectMode(this._latestSegments, _.map(this._latestStops, s => s?.geometry?.coordinates).filter(coords => coords != undefined));
    }

    public init(defaultLine: LineString, stops: Feature<Point>[]) {
        this._isLoading$.next(true);
        this._clear();
        this._clearRoutedLine();
        this._defaultLine = defaultLine;

        const first = _.first(defaultLine.coordinates);
        const last = _.last(defaultLine.coordinates);

        // to fix if there not are any stop in the first or last coordinate
        const firsStop = _.first(stops)?.geometry.coordinates;
        if (firsStop !== first) {
            stops.unshift(turf.point(first));
        }

        const lastStop = _.last(stops)?.geometry.coordinates;
        if (lastStop !== last) {
            stops.push(turf.point(last));
        }
        // -------------

        this._latestStops = stops;
        this._latestSegments = this._cutLineIntoSeveralSegments(this._defaultLine, this._latestStops);
        this._initSelectMode(this._latestSegments, _.map(this._latestStops, s => s?.geometry?.coordinates).filter(coords => coords != undefined));

        this._isLoading$.next(false);

        this._triggerFeaturesToRender();
    }

    public canUndo() {
        if (this._clickedCoordinates.length === 0 && this._drawMode === DRAW_MODE.BEFORE) {
            return this.startCoordinate !== undefined;
        } else if (this._clickedCoordinates.length === 0) {
            return false;
        }

        return true;
    }

    public undo() {
        if (this._clickedCoordinates.length === 0 && this._drawMode === DRAW_MODE.BEFORE) {
            // if before, then undo the start position
            this.startCoordinate = undefined;
        } else {
            // remove the latest known
            this._clickedCoordinates.pop();
            this._clickedLineSegments.pop();
        }

        // make the end coordinates the last one
        if (this._drawMode === DRAW_MODE.AFTER) {
            this.endCoordinate = _.last(this._clickedCoordinates) || this._getLastCoordinate();
        }

        this._calculateNewLineString();
        this._triggerFeaturesToRender();
    }

    public deleteSelected() {
        // remove the selected linesegments (change properties to look different)
        _.each(this._removedLineSegmentIds, id => {
            const segment = this._lineSegments[id];
            segment.properties.mode = 'view';
            segment.properties.type = 'reference';
        });

        this._triggerFeaturesToRender();
    }

    public initDrawMode() {
        this._drawMode = DRAW_MODE.MIDDLE;

        if (this._removedLineSegmentIds.length > 0) {
            // Determine the start and end coordinate depending on
            // which segments where removed
            const minSegmentId = _.min(this._removedLineSegmentIds);
            if (minSegmentId !== 0) {
                const segment = this._lineSegments[minSegmentId];
                if (segment?.geometry?.coordinates) {
                    this.startCoordinate = _.first(segment.geometry.coordinates);
                }
            } else {
                this.startCoordinate = undefined;
                this._drawMode = DRAW_MODE.BEFORE;
            }

            const maxSegmentId = _.max(this._removedLineSegmentIds);
            if (maxSegmentId !== (this._lineSegments.length - 1)) {
                const segment = this._lineSegments[maxSegmentId];
                if (segment?.geometry?.coordinates) {
                    this.endCoordinate = _.last(segment.geometry.coordinates);
                }
            } else {
                this.endCoordinate = undefined;
                this._drawMode = DRAW_MODE.AFTER;
            }
        } else {
            this._drawMode = DRAW_MODE.UNKNOWN;
        }

        _.each(this._lineSegments, segment => {
            segment.properties.mode = 'view';
        });

        this._triggerFeaturesToRender();
    }

    private _clearRoutedLine() {
        this.startCoordinate = undefined;
        this.endCoordinate = undefined;
        this._removedLineSegmentIds = [];
        this._clickedCoordinates = [];
        this._clickedLineSegments = [];
        this._lineSegments = [];
        this._wayPoints = [];
        this._drawMode = DRAW_MODE.UNKNOWN;
        this._constructedNewFeature = undefined;
        this._drawChange$.next([]);
    }

    private _clear() {
        this._latestSegments = undefined;
        this._latestStops = undefined;
        this._defaultLine = undefined;
    }

    private _cutLineIntoSeveralSegments(line: LineString, points: Feature<Point>[]) {
        const allLines = [];
        for (let index = 0; index < points.length - 1; index++) {
            const segment = turf.lineSlice(points[index], points[index + 1], line);
            segment.id = index;
            segment.properties.id = index;
            allLines.push(segment);
        }
        return allLines;
    }

    private _initSelectMode(lineSegments: GeoJSON.Feature<any>[], wayPoints: number[][]) {
        this._removedLineSegmentIds = [];
        this._clickedCoordinates = [];
        this._clickedLineSegments = [];

        this._lineSegments = lineSegments;
        this._wayPoints = wayPoints;

        // set the start coordinate and the endcoordinate
        const firstSegment = _.first(this._lineSegments);
        if (firstSegment?.geometry?.coordinates) {
            this.startCoordinate = _.first(firstSegment.geometry.coordinates);
        }

        const lastSegment = _.last(this._lineSegments);
        if (lastSegment?.geometry?.coordinates) {
            this.endCoordinate = _.last(lastSegment.geometry.coordinates);
        }

        // set all linesegments to view
        _.each(this._lineSegments, segment => {
            segment.properties.mode = 'view';
            segment.properties.type = 'line';
        });

        this._drawMode = DRAW_MODE.UNKNOWN;

        this._triggerFeaturesToRender();
    }

    // get the last coordinate depending on which segments are removed
    private _getLastCoordinate(): number[] {
        if (this._removedLineSegmentIds.length > 0) {
            const maxSegmentId = _.max(this._removedLineSegmentIds);
            if (maxSegmentId !== (this._lineSegments.length - 1)) {
                const segment = this._lineSegments[maxSegmentId];
                if (segment?.geometry?.coordinates) {
                    return _.last(segment.geometry.coordinates);
                }
            }
        }
        const lastSegment = _.last(this._lineSegments);
        if (lastSegment?.geometry?.coordinates) {
            return _.last(lastSegment.geometry.coordinates);
        }
        return null;
    }

    // get the first coordinate depending on which segments are removed
    private _getFirstCoordinate(): number[] {
        if (this._removedLineSegmentIds.length > 0) {
            const minSegmentId = _.max(this._removedLineSegmentIds);
            const segment = this._lineSegments[minSegmentId];
            if (segment?.geometry?.coordinates) {
                return _.last(segment.geometry.coordinates);
            }
        }

        const firstSegment = _.first(this._lineSegments);
        if (firstSegment?.geometry?.coordinates) {
            return _.first(firstSegment.geometry.coordinates);
        }
        return null;
    }

    // Calculate the new feature line string
    private _calculateNewLineString(): void {
        if (this._lineSegments.length === 0) {
            return;
        }

        const allSegments = this._calcNewLineSegments();
        if (allSegments.length > 0) {
            const fc = turf.featureCollection(allSegments);
            const lineString = turf.lineString(turf.coordAll(fc));
            this._constructedNewFeature = lineString;
        } else {
            this._constructedNewFeature = undefined;
        }
    }

    // calculate all the segments and order them correctly
    private _calcNewLineSegments() {
        let allSegments = [];
        if (this._removedLineSegmentIds.length > 0) {
            const minSegmentId = _.min(this._removedLineSegmentIds);
            if (minSegmentId !== undefined) {
                const prevLineSegment = this._lineSegments.slice(0, minSegmentId); // kan bli noll här!!
                allSegments = allSegments.concat(prevLineSegment);
            }

            if (this._clickedLineSegments.length > 0) {
                allSegments = allSegments.concat(this._clickedLineSegments);
            }

            const maxSegmentId = _.max(this._removedLineSegmentIds);
            if (maxSegmentId !== undefined) {
                const laterLineSegments = this._lineSegments.slice(maxSegmentId + 1);
                allSegments = allSegments.concat(laterLineSegments);
            }
        } else {
            if (this._drawMode === DRAW_MODE.BEFORE) {
                allSegments = this._clickedLineSegments.slice().concat(this._lineSegments.slice());
            } else if (this._drawMode === DRAW_MODE.AFTER) {
                allSegments = this._lineSegments.slice().concat(this._clickedLineSegments.slice());
            }
        }

        return allSegments;
    }

    private _triggerFeaturesToRender(): void {
        const allFeatures = this._lineSegments.concat(this._clickedLineSegments);

        // construct point-feature from the coordinates array
        const checkPoints = this._clickedCoordinates.concat(this._wayPoints);
        _.each(checkPoints, cord => {
            const val = turf.point(cord, {
                type: 'checkpoint'
            });
            allFeatures.push(val);
        });

        //#region start and end coordinates
        if (this.startCoordinate) {
            const startPoint = turf.point(this.startCoordinate, {
                type: 'refpoint',
                pointType: 'start',
                color: '#32a852'
            });
            allFeatures.push(startPoint);
        }

        if (this.endCoordinate) {
            const endPoint = turf.point(this.endCoordinate, {
                type: 'refpoint',
                pointType: 'end',
                color: '#fc2145'
            });
            allFeatures.push(endPoint);
        }
        //#endregion


        this._drawChange$.next(allFeatures);
    }

}
